import type { UseFormReturn } from "react-hook-form";
import { FileSignature, Loader2 } from "lucide-react";
import { Button } from "@components/ui/button";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@components/ui/form";
import { Input } from "@components/ui/input";
import { RadioGroup, RadioGroupItem } from "@components/ui/radio-group";
import { Alert } from "@shared-kernel/primary/shared/alert";
import { CONTRACT_SIGNATORY_TYPE, isDelegateSignatoryType } from "@academy-context/shared/domain/types/contract-signatory";
import type { ContractSignatoryFormInputs } from "../form-validation/contract-signatory-form.schema";
import type { FormSubmit } from "@shared-kernel/primary/shared/api-errors/action-form";
import { formatPersonName } from "@academy-context/shared/domain/services/contract-signatory";

interface Props {
  legalRepresentative: { firstName: string; lastName: string };
  providerEmail: string;
  form: UseFormReturn<ContractSignatoryFormInputs>;
  onSubmit: FormSubmit;
}

export const ContractSignatoryForm = ({ legalRepresentative, providerEmail, form, onSubmit }: Props) => {
  const isInitializing = form.formState.isSubmitting;

  const legalRepresentativeName = formatPersonName(legalRepresentative);
  const signatoryType = form.watch("type");
  const isDelegate = isDelegateSignatoryType(signatoryType);

  return (
    <Form {...form}>
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="rounded-md border bg-muted/40 p-3 text-sm">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Signataire prévu</p>
          <p className="mt-1 font-medium">{legalRepresentativeName}, représentant légal</p>
          <p className="text-muted-foreground">{providerEmail}</p>
        </div>

        <FormField
          control={form.control}
          name="type"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <RadioGroup onValueChange={field.onChange} value={field.value ?? ""} className="flex flex-col space-y-1">
                  <FormItem className="flex items-center space-x-3 space-y-0">
                    <FormControl>
                      <RadioGroupItem value={CONTRACT_SIGNATORY_TYPE.LEGAL_REPRESENTATIVE} />
                    </FormControl>
                    <FormLabel className="font-normal">Je suis {legalRepresentativeName}, représentant légal</FormLabel>
                  </FormItem>
                  <FormItem className="flex items-center space-x-3 space-y-0">
                    <FormControl>
                      <RadioGroupItem value={CONTRACT_SIGNATORY_TYPE.DELEGATE} />
                    </FormControl>
                    <FormLabel className="font-normal">Je signe en délégation du représentant légal</FormLabel>
                  </FormItem>
                </RadioGroup>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {isDelegate && (
          <div className="space-y-4">
            <Alert variant="info" className="text-sm">
              Vous signez en délégation du représentant légal {legalRepresentativeName}.
            </Alert>
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Prénom</FormLabel>
                    <FormControl>
                      <Input {...field} autoComplete="given-name" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nom</FormLabel>
                    <FormControl>
                      <Input {...field} autoComplete="family-name" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>E-mail</FormLabel>
                  <FormControl>
                    <Input {...field} type="email" autoComplete="email" />
                  </FormControl>
                  <FormDescription>Le code de signature vous sera envoyé à cette adresse.</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        )}

        {signatoryType && (
          <Button type="submit" disabled={isInitializing} size="sm">
            {isInitializing ? <Loader2 className="mr-2 size-4 animate-spin" /> : <FileSignature className="mr-2 size-4" />}
            {isInitializing ? "Chargement..." : "Signer le contrat"}
          </Button>
        )}
      </form>
    </Form>
  );
};
